import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  DirectionsRenderer,
  useJsApiLoader,
} from "@react-google-maps/api";
import "./MapComponent.css";

const libraries = ["places"];
const containerStyle = { width: "100%", height: "100%" };

const MapComponent = ({ from, to, currentLocation: initialCurrentLocation, setTripDuration }) => {
  const [center, setCenter] = useState(null);
  const [destination, setDestination] = useState(null);
  const [directions, setDirections] = useState(null);
  const [destinationLabel, setDestinationLabel] = useState("");
  const [destinationInput, setDestinationInput] = useState("");
  const [sourceLabel, setSourceLabel] = useState(from || "Your current location");
  const [currentLocation, setCurrentLocation] = useState(initialCurrentLocation);
  const [locationError, setLocationError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [showRouteInfo, setShowRouteInfo] = useState(false);
  const watchIdRef = useRef(null);

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Get user's current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          resolve(location);
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  // Start tracking user's location
  const startTracking = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      return;
    }

    setIsTracking(true);
    setSourceLabel("Your live location");
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentLocation(newLocation);
        setCenter(newLocation);

        // If destination is set, recalculate route
        if (destination) {
          calculateRoute(newLocation, destination);
        }
      },
      (error) => {
        setLocationError(error.message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 5000
      }
    );
  };

  // Stop tracking
  const stopTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
    setSourceLabel(from || "Source location");
  };

  // Initialize with current location when component mounts
  useEffect(() => {
    // Set initial source label from props
    setSourceLabel(from || "Your current location");

    if (!initialCurrentLocation) {
      getCurrentLocation()
        .then(location => {
          setCurrentLocation(location);
          // Only set center to current location if we're tracking
          if (isTracking) {
            setCenter(location);
          }
        })
        .catch(error => {
          console.error("Error getting initial location:", error);
          setLocationError(error.message);
        });
    } else {
      setCenter(initialCurrentLocation);
    }

    return () => {
      stopTracking();
    };
  }, [initialCurrentLocation, from]);

  // Calculate route when destination changes
  useEffect(() => {
    if (isLoaded && center && destination) {
      calculateRoute(center, destination);
    }
  }, [isLoaded, center, destination]);

  // Handle from/to props changes
  useEffect(() => {
    if (!isLoaded || !to) return;

    const geocodeAddress = (address) => {
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results[0]) {
            resolve(results[0].geometry.location.toJSON());
          } else {
            reject(`Failed to geocode ${address}: ${status}`);
          }
        });
      });
    };

    const fetchDestination = async () => {
      try {
        const destLoc = await geocodeAddress(to);
        setDestination(destLoc);
        setDestinationLabel(to);
        setDestinationInput(to);
      } catch (err) {
        console.error("Failed to geocode destination:", err);
      }
    };

    fetchDestination();
  }, [isLoaded, to]);

  const calculateRoute = (origin, destination) => {
    if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
      console.error("Google Maps API is not loaded yet.");
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);

          const duration = result.routes[0]?.legs[0]?.duration?.text;
          const distance = result.routes[0]?.legs[0]?.distance?.text;
          
          if (duration && setTripDuration) {
            setTripDuration(duration);
          }

          // Extract route steps
          if (result.routes[0]?.legs[0]?.steps) {
            const steps = result.routes[0].legs[0].steps.map(step => step.instructions.replace(/<[^>]*>/g, ''));
            setRouteInfo({
              duration,
              distance,
              steps,
              startAddress: result.routes[0].legs[0].start_address,
              endAddress: result.routes[0].legs[0].end_address
            });
            setShowRouteInfo(true);
          }
        }
      }
    );
  };

  const handleDestinationSearch = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const loc = place.geometry.location.toJSON();
      setDestination(loc);
      setDestinationInput(place.formatted_address || "Destination");

      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        console.error("Google Maps Geocoder not loaded");
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: loc }, (results, status) => {
        if (status === "OK" && results[0]) {
          setDestinationLabel(results[0].formatted_address);
        }
      });
    }
  };

  useEffect(() => {
    if (from && to) {
      calculateRoute(from, to);
    }
  }, [from, to]);

  const getRoute = () => {
    if (!center || !destination) return;
    calculateRoute(center, destination);
  };

  // Map load callback
  const onMapLoad = React.useCallback((map) => {
    mapRef.current = map;
  }, []);

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <div className="map-component-container">
      {/* Search Section */}
      <div className="search-section">
        <div className="search-group">
          <label>From:</label>
          <div className="location-info">
            {currentLocation ? (
              <>
                <span>Your live location</span>
                <button
                  onClick={isTracking ? stopTracking : startTracking}
                  className={`track-button ${isTracking ? 'tracking' : ''}`}
                >
                  {isTracking ? 'Stop Tracking' : 'Track Live'}
                </button>
              </>
            ) : (
              <span>{locationError || "Getting your location..."}</span>
            )}
          </div>
        </div>

        <div className="search-group">
          <label>To:</label>
          <Autocomplete
            onLoad={(auto) => (autocompleteRef.current = auto)}
            onPlaceChanged={handleDestinationSearch}
          >
            <input
              className="location-search-input"
              placeholder="Enter destination"
              value={destinationInput}
              onChange={(e) => setDestinationInput(e.target.value)}
            />
          </Autocomplete>
        </div>
      </div>

      {/* Button */}
      {/* <div className="button-container">
        <button className="get-route-button" onClick={getRoute}>
          Get Route
        </button>
      </div> */}

      {/* Route Information Panel */}
      {/* {showRouteInfo && routeInfo && (
        <div className="route-info-panel" style={{
          background: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <strong>Trip Route: {destinationLabel || 'Destination'}</strong>
            </div>
            <button 
              onClick={() => setShowRouteInfo(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#6c757d'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ marginBottom: '10px' }}>
            <strong>From:</strong> {routeInfo.startAddress || 'Your live location'} <br/>
            <strong>To:</strong> {routeInfo.endAddress || destinationLabel}
          </div>
          
          <div style={{ marginBottom: '10px' }}>
            <strong>Distance:</strong> {routeInfo.distance} | <strong>Duration:</strong> {routeInfo.duration}
          </div>
          
           {routeInfo.steps && routeInfo.steps.length > 0 && (
            <div style={{
              maxHeight: '150px',
              overflowY: 'auto',
              padding: '10px',
              background: 'white',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}>
              <strong>Route Steps:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {routeInfo.steps.slice(0, 10).map((step, index) => (
                  <li key={index} style={{ fontSize: '12px', marginBottom: '5px' }}>
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )} */}

      {/* Map */}
      <div className="mapview-container">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center || { lat: 20.5937, lng: 78.9629 }}
          zoom={14}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            mapTypeId: 'roadmap',
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "transit",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          }}
          onLoad={onMapLoad}
        >
          {currentLocation && (
            <Marker
              position={currentLocation}
              icon={{
                url: 'https://maps.google.com/mapfiles/kml/shapes/truck.png',
                scaledSize: new window.google.maps.Size(40, 40),
              }}
              label="📍"
            />
          )}

          {destination && (
            <Marker 
              position={destination} 
              label="B"
              icon={{
                url: 'https://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png',
                scaledSize: new window.google.maps.Size(30, 30),
              }}
            />
          )}
          
          {directions && (
            <DirectionsRenderer 
              directions={directions}
              options={{
                suppressMarkers: false,
                polylineOptions: {
                  strokeColor: '#4285f4',
                  strokeWeight: 5,
                  strokeOpacity: 0.8,
                },
                markerOptions: {
                  visible: true
                }
              }}
            />
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default MapComponent;